import { useColorModeValue } from "@chakra-ui/react";
import { useToast } from "@chakra-ui/toast";
import React from "react";
import { useHistory } from "react-router";
import { QuoteApi } from "../../api/QuoteApi";
import useUser from "../../hooks/useUser";
import { MetricType } from "../../lib/types/MetricType";
import { QuoteType } from "../../lib/types/QuoteType";
import { isQuoteOwner } from "../../lib/UserPermissions";
import { QuoteService } from "../../services/quote.service";
import { QuoteContext } from "../../state/quote-context";
import { quoteReducer } from "../../state/quote-reducer";
import { initialQuoteState } from "../../state/quote-state";

export interface QuoteProviderProps {
  children: any;
  id: number;
  quoteType: QuoteType;
}

function QuoteContextProvider({ children, id, quoteType }: QuoteProviderProps) {
  const toast = useToast();
  const history = useHistory();
  const user = useUser();
  const [state, dispatch] = React.useReducer(quoteReducer, initialQuoteState);
  const toastVariant = useColorModeValue("subtle", "solid");

  React.useEffect(() => {
    if (user.user?.email) {
      dispatch({
        type: "USER_CHANGED",
        payload: user.user?.email,
      });
    }
  }, [user]);

  React.useEffect(() => {
    dispatch({
      type: "QUOTE_TYPE_CHANGE",
      payload: quoteType,
    });
  }, [quoteType]);

  const sendToast = React.useCallback(
    (
      title: string,
      description: string,
      status: "info" | "warning" | "success" | "error" | undefined
    ) => {
      toast({
        title,
        description,
        status,
        variant: toastVariant,
        isClosable: true,
        position: "top-right",
      });
    },
    [toast, toastVariant]
  );

  const fetchQuote = React.useCallback(async () => {
    if (!user.user?.email) {
      dispatch({ type: "QUOTE_LOADING" });
      return;
    }

    dispatch({ type: "QUOTE_LOADING" });

    try {
      const loadedQuote = await QuoteService.loadQuoteByType(quoteType, id);

      if (Object.keys(loadedQuote.warnings).length > 0) {
        dispatch({
          type: "QUOTE_CALCULATE_WARNING",
          payload: loadedQuote.warnings,
        });
      }

      dispatch({
        type: "QUOTE_LOADED",
        payload: {
          quote: loadedQuote.quote,
          calculation: loadedQuote.calculation,
          quoteMetadata: loadedQuote.metadata,
          rateLock: loadedQuote.metadata.rateLock,
        },
      });

      dispatch({
        type: "QUOTE_TYPE_CHANGE",
        payload: loadedQuote.quote.quoteType,
      });
    } catch (err) {
      sendToast(
        "Quote Error",
        "An error has occurred while trying to load the quote",
        "error"
      );

      history.push("/");
    }
  }, [id, quoteType, sendToast, history, user.user?.email]);

  const saveQuoteMetrics = async (metric: MetricType) => {
    if (!state.quote?.username) return;

    try {
      await QuoteService.saveMetricByType(
        id,
        metric,
        quoteType,
        state.quote,
        state.quoteCalculation
      );
    } catch (err) {
      console.error(err);
    }
  };

  const saveQuoteHelper = async (quote: any, isCopy = false) => {
    if (!state.hasCalculated || !state.quote) {
      return;
    }

    dispatch({
      type: "QUOTE_SAVING",
    });

    // We merge the quote from state and the incoming quote
    // so that in the case of a copy we get the updated name
    const quoteToSave = {
      ...state.quote,
      quoteName: quote.quoteName,
    };

    if (quote.username) {
      quoteToSave.username = quote.username;
    }

    try {
      const result = await QuoteService.saveQuoteByType(
        id,
        quoteType,
        quoteToSave,
        state.quoteCalculation,
        isCopy
      );

      dispatch({
        type: "QUOTE_SAVED",
        payload: {
          quote: result.quote,
          calculation: result.calculation,
        },
      });

      sendToast("Quote Saved", "The quote has been saved.", "success");

      if (result.id !== +id) {
        history.push(
          `/quotes/${result.id}/edit?type=${quoteType.toLowerCase()}#top`
        );

        return;
      }

      fetchQuote();
    } catch (err) {
      dispatch({ type: "QUOTE_SAVE_ERROR" });

      sendToast(
        "Error Saving Quote",
        "The quote could not be saved. Please try again.",
        "error"
      );
    }
  };

  const submitQuoteHandler = async (quote: any) => {
    dispatch({
      type: "QUOTE_SUBMITTING",
    });

    try {
      const hasSubmittedQuote = await QuoteService.submitQuoteByType(
        id,
        quoteType,
        state.quote,
        state.quoteCalculation,
        user.role === "External"
      );

      if (hasSubmittedQuote) {
        dispatch({
          type: "QUOTE_SUBMITTED",
          payload: true,
        });

        user.role === "External"
          ? sendToast(
              "Quote Submitted For Review",
              "The quote has been submitted for review.",
              "success"
            )
          : sendToast(
              "Quote Submitted",
              "The quote has been submitted to Salesforce",
              "success"
            );

        await saveQuoteHelper({ ...state.quote, hasSubmittedQuote }, false);
      } else {
        dispatch({
          type: "QUOTE_SUBMITTED_ERROR",
        });
      }
    } catch (err) {
      dispatch({
        type: "QUOTE_SUBMITTED_ERROR",
      });

      sendToast(
        "Error Submitting Quote",
        "The quote could not be submitted. Please try again.",
        "error"
      );
    }
  };

  const calculateQuote = React.useCallback(
    async (quote: any) => {
      dispatch({
        type: "QUOTE_UPDATED",
        payload: {
          quote: quote,
        },
      });

      dispatch({ type: "QUOTE_CALCULATING" });

      try {
        const calculatedQuote = await QuoteService.calculateQuoteByType(
          quoteType,
          quote,
          id
        );

        if (!calculatedQuote.warnings?.errors) {
          sendToast(
            "Quote Calculated",
            "The quote has been successfully calculated.",
            "success"
          );
        } else {
          sendToast(
            "Quote Calculated with Errors",
            "The quote has been calculated but with errors.",
            "warning"
          );
        }

        dispatch({
          type: "QUOTE_CALCULATED",
          payload: {
            quote: calculatedQuote.quote,
            calculation: calculatedQuote.calculation,
            hasCalculated: true,
          },
        });

        if (calculatedQuote.warnings) {
          dispatch({
            type: "QUOTE_CALCULATE_WARNING",
            payload: calculatedQuote.warnings,
          });
        }
      } catch (err) {
        console.log(err);

        dispatch({
          type: "QUOTE_CALCULATE_ERROR",
        });

        sendToast(
          "Calculation Error",
          "The calculation encountered an error. Please try again.",
          "error"
        );
      }
    },
    [quoteType, sendToast, id]
  );

  const deleteQuote = React.useCallback(
    async (quote: any) => {
      QuoteApi.deactivateQuote(id)
        .then((res) => {
          toast({
            status: "success",
            position: "top-right",
            title: "Quote Deleted",
            description: "The quote has been successfully deleted",
            isClosable: true,
            variant: toastVariant,
          });
        })
        .catch((err) => {
          toast({
            status: "error",
            position: "top-right",
            title: "Unable to Delete Quote",
            description: "The quote could not be deleted",
            isClosable: true,
            variant: toastVariant,
          });
        })
        .finally(() => {
          history.push("/quotes/my-quotes");
        });
    },
    [history, toast, id, toastVariant]
  );

  React.useEffect(() => {
    if (!id) return;

    fetchQuote();
  }, [id, fetchQuote]);

  return (
    <QuoteContext.Provider
      value={{
        state,
        dispatch,
        fetchQuote,
        saveQuoteHelper,
        calculateQuote,
        deleteQuote,
        saveQuoteMetrics,
        submitQuoteHandler,
        quoteType,
        isOwned: state.isNewQuote
          ? true
          : isQuoteOwner(
              state.quote?.username,
              user.sfProfile,
              user.user?.email
            ),
        quoteId: id,
      }}
    >
      {children}
    </QuoteContext.Provider>
  );
}

export default QuoteContextProvider;
